/**
 * AeActivityLog — /log/ae
 *
 * Self-reported daily activity log for AE reps.
 * Sections: Account-Touching Activity Totals · Per-Account Log · Notes
 *
 * Writes to TWO destinations on Save:
 *   1. daily_activity_actual (via PUT /api/me/activity/daily) — KPI aggregates
 *   2. activity_log (via logActivity() fire-and-forget) — per-action audit trail
 *
 * BUILD-13 Item 1 fix (2026-05-15):
 *   - Added logActivity() calls for every AE workflow action on handleSave
 *   - Writes one summary row + one row per per-account entry
 *   - tenant_id auto-stamped by logActivity() via getSession().tenantId
 *   - user_id auto-stamped by logActivity() via getSession().id (SessionUser.id)
 *   - localStorage write retained as local cache (resilience, not primary store)
 *
 * Palette: #FAFAF7 bg · white cards · #0B0B0F black · #DC2626 red
 */

import { useState, useEffect, useRef } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { useUnloadWarning } from "@/lib/useUnloadWarning";
import { BackBar } from "@/components/BackBar";
import { api, getSession } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import { logActivity } from "@/lib/activity-log";
import { useAccounts } from "@/pages/dashboards/live-data";
import {
  saveAeLog,
  getAeLog,
  getAeLogFromApi,
  emptyAeFields,
  todayString,
  type AeFields,
  type AeAccountEntry,
  type AeLog,
} from "@/lib/activity-log-store";

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

const ACTIVITY_TYPES: AeAccountEntry["activityType"][] = [
  "Call", "Email", "Meeting", "Demo", "Proposal", "Contract", "Other",
];

// ─── Numeric input ────────────────────────────────────────────────────────────

function NumInput({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  hint?: string;
}) {
  const filled = value > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className="text-[10px] uppercase tracking-[0.16em] font-semibold leading-tight"
        style={{ color: "#6B6B70" }}
      >
        {label}
      </label>
      <input
        type="number"
        min={0}
        value={value === 0 ? "" : value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
        className="w-full text-lg font-semibold tabular-nums rounded-md border border-neutral-200 px-2.5 py-2 focus:outline-none bg-white transition-all"
        style={{ color: filled ? MI6_BLACK : "#8E8E94" }}
        onFocus={(e) => {
          e.target.style.boxShadow = `0 0 0 2px ${MI6_RED}33`;
          e.target.style.borderColor = MI6_RED;
        }}
        onBlur={(e) => {
          e.target.style.boxShadow = "";
          e.target.style.borderColor = "";
        }}
      />
      {hint && (
        <span className="text-[10px] text-neutral-500 leading-tight">{hint}</span>
      )}
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({
  title,
  children,
  accent,
}: {
  title: string;
  children: React.ReactNode;
  accent?: string;
}) {
  return (
    <div
      className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden"
      style={{ borderLeft: `4px solid ${accent ?? MI6_BLACK}` }}
    >
      <div className="px-5 pt-4 pb-2 border-b border-neutral-100">
        <div
          className="text-[10px] uppercase tracking-[0.22em] font-bold"
          style={{ color: accent ?? MI6_BLACK }}
        >
          {title}
        </div>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

// ─── Account picker (autocomplete from AE's owned accounts) ──────────────────
// Bug B fix: replaces free-text Company input so account_id is FK-resolved.

interface AccountOption { id: number; name: string; }

function AccountPickerInput({
  value,
  accountId,
  accounts,
  onChange,
}: {
  value: string;
  accountId: number | null;
  accounts: AccountOption[];
  onChange: (name: string, id: number | null) => void;
}) {
  const [query, setQuery]   = useState(value);
  const [open, setOpen]     = useState(false);
  const containerRef        = useRef<HTMLDivElement>(null);

  // Keep local query in sync if parent resets entry
  useEffect(() => { setQuery(value); }, [value]);

  const filtered = query.trim().length === 0
    ? accounts.slice(0, 8)
    : accounts
        .filter((a) => a.name.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8);

  function handleSelect(a: AccountOption) {
    setQuery(a.name);
    setOpen(false);
    onChange(a.name, a.id);
  }

  // Close dropdown on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const isResolved = accountId !== null;

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        autoComplete="off"
        placeholder="Search accounts…"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Clear resolved id when user edits manually
          onChange(e.target.value, null);
        }}
        onFocus={() => setOpen(true)}
        className="w-full rounded-lg border bg-white px-3 py-2 text-sm text-neutral-800 placeholder-neutral-300 focus:outline-none"
        style={{
          borderColor: isResolved ? `${MI6_RED}80` : "#E5E5E7",
          boxShadow: isResolved ? `0 0 0 1px ${MI6_RED}30` : undefined,
        }}
      />
      {isResolved && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold" style={{ color: MI6_RED }}>
          linked
        </span>
      )}
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg text-sm overflow-hidden"
          style={{ maxHeight: 200, overflowY: "auto" }}
        >
          {filtered.map((a) => (
            <li
              key={a.id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(a); }}
              className="px-3 py-2 cursor-pointer hover:bg-neutral-50 text-neutral-800"
            >
              {a.name}
            </li>
          ))}
        </ul>
      )}
      {open && filtered.length === 0 && query.trim().length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg text-sm px-3 py-2 text-neutral-400">
          No matching accounts
        </div>
      )}
    </div>
  );
}

// ─── Per-account entry card ───────────────────────────────────────────────────

function AccountEntryCard({
  entry,
  index,
  accounts,
  onChange,
  onRemove,
}: {
  entry: AeAccountEntry;
  index: number;
  accounts: AccountOption[];
  onChange: (updated: AeAccountEntry) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-3 relative">
      <div className="flex items-center justify-between">
        <span
          className="text-[9px] uppercase tracking-[0.2em] font-bold"
          style={{ color: MI6_BLACK }}
        >
          Account #{index + 1}
        </span>
        <button
          onClick={onRemove}
          className="text-neutral-500 hover:text-red-500 transition-colors text-sm font-bold"
          title="Remove"
          aria-label="Remove entry"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Account picker — Bug B fix: autocomplete resolves account_id FK */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">
            Account
          </label>
          <AccountPickerInput
            value={entry.accountName}
            accountId={entry.account_id}
            accounts={accounts}
            onChange={(name, id) => onChange({ ...entry, accountName: name, account_id: id })}
          />
          {!entry.account_id && entry.accountName.trim().length > 0 && (
            <span className="text-[10px] text-orange-600">Select from the list to link the account.</span>
          )}
        </div>

        {/* Activity type */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">
            Activity Type
          </label>
          <select
            value={entry.activityType}
            onChange={(e) =>
              onChange({ ...entry, activityType: e.target.value as AeAccountEntry["activityType"] })
            }
            className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 focus:outline-none"
            style={{ fontWeight: 600, color: MI6_BLACK }}
            onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${MI6_RED}40`)}
            onBlur={(e) => (e.target.style.boxShadow = "")}
          >
            {ACTIVITY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Outcome note */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">
            Outcome / Note
          </label>
          <input
            type="text"
            value={entry.outcomeNote}
            onChange={(e) => onChange({ ...entry, outcomeNote: e.target.value })}
            placeholder="Brief outcome…"
            className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 placeholder-neutral-300 focus:outline-none"
            onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${MI6_RED}40`)}
            onBlur={(e) => (e.target.style.boxShadow = "")}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

interface AeActivityLogProps {
  modal?: boolean;
  onSaved?: () => void;
}

export default function AeActivityLog({ modal = false, onSaved }: AeActivityLogProps) {
  const { toast } = useToast();
  const [date, setDate] = useState(todayString());
  const [fields, setFields] = useState<AeFields>(emptyAeFields());
  const [accountEntries, setAccountEntries] = useState<AeAccountEntry[]>([]);
  const [notes, setNotes] = useState("");
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Bug B fix: load AE's owned accounts for the account picker
  const accountsRes = useAccounts();
  const accountOptions: AccountOption[] = (accountsRes.data ?? []).map((a) => ({ id: a.id, name: a.name }));

  const hasFieldInput = Object.values(fields).some((v) => Number(v) > 0);
  const hasNotes = notes.trim().length > 0;
  const hasEntries = accountEntries.length > 0;
  useUnloadWarning(
    savedAt === null && (hasFieldInput || hasNotes || hasEntries),
    "You have unsaved AE activity. Click Save before leaving."
  );

  // Load existing log when date changes.
  // Primary source: API (Supabase daily_activity_actual) — hydrates after hard refresh / new device.
  // Fallback: localStorage cache for pre-migration entries or offline mode.
  const session = getSession();
  useEffect(() => {
    const uid = session?.id ?? "current_user";
    const aeName = session?.name ?? "AE";

    // Show cached data immediately (no flicker) then replace with API truth.
    const cached = getAeLog(uid, date) ?? getAeLog("current_user", date);
    if (cached) {
      setFields(cached.fields);
      setAccountEntries(cached.accountEntries ?? []);
      setNotes(cached.notes);
      setSavedAt(cached.saved_at);
    } else {
      setFields(emptyAeFields());
      setAccountEntries([]);
      setNotes("");
      setSavedAt(null);
    }

    // Then hydrate from server (source of truth post-migration).
    if (session?.id) {
      getAeLogFromApi(uid, aeName, date).then((apiLog) => {
        if (apiLog) {
          setFields(apiLog.fields);
          setAccountEntries(apiLog.accountEntries ?? []);
          setNotes(apiLog.notes);
          const t = new Date(apiLog.saved_at);
          const timeStr = isNaN(t.getTime())
            ? apiLog.saved_at
            : t.toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" });
          setSavedAt(timeStr);
        }
      }).catch(() => { /* API unreachable — cache already shown */ });
    }
  }, [date, session?.id]);

  function set(k: keyof AeFields, v: number) {
    setFields((f) => ({ ...f, [k]: v }));
    setSavedAt(null);
  }

  function addAccountEntry() {
    const newEntry: AeAccountEntry = {
      id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
      accountName: "",
      account_id: null,  // Bug B fix: will be set by account picker selection
      activityType: "Call",
      outcomeNote: "",
    };
    setAccountEntries((prev) => [...prev, newEntry]);
    setSavedAt(null);
  }

  function updateEntry(id: string, updated: AeAccountEntry) {
    setAccountEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
    setSavedAt(null);
  }

  function removeEntry(id: string) {
    setAccountEntries((prev) => prev.filter((e) => e.id !== id));
    setSavedAt(null);
  }

  async function handleSave() {
    // Block save if session is not available.
    if (!session?.id) {
      toast({
        title: "Session unavailable",
        description: "Your session could not be verified. Please reload the page and try again.",
        variant: "destructive",
      });
      return;
    }
    const now = new Date().toISOString();
    const userId = session.id;
    const log: AeLog = {
      user_id: userId,
      user_name: session.name ?? "AE",
      role: "ae",
      date,
      fields,
      accountEntries,
      notes,
      saved_at: now,
    };

    // Build supplemental note so richer fields (emails, proposals, contracts,
    // account entries) are preserved in the server-side notes column even though
    // daily_activity_actual only has top-level numeric columns for the primary
    // KPI fields.  Account entries are JSON-encoded for future extraction.
    const supplement: Record<string, unknown> = {};
    if (fields.emailsSent > 0)      supplement.emailsSent      = fields.emailsSent;
    if (fields.proposalsSent > 0)   supplement.proposalsSent   = fields.proposalsSent;
    if (fields.contractsSent > 0)   supplement.contractsSent   = fields.contractsSent;
    if (accountEntries.length > 0)  supplement.accountEntries  = accountEntries;
    const supplementStr =
      Object.keys(supplement).length > 0
        ? `\n[supplement:${JSON.stringify(supplement)}]`
        : "";
    const serverNotes = notes + supplementStr || null;

    try {
      // ── Primary Supabase write: daily_activity_actual (KPI aggregates) ──────
      await api("/api/me/activity/daily", {
        method: "PUT",
        body: JSON.stringify({
          activity_date:       date,
          outreach:            fields.callsMade,
          emails_sent:         fields.emailsSent,        // Fix 1: was silently dropped
          first_meetings:      fields.meetingsHeld,
          qualified_meetings:  fields.demosGiven,
          proposals_sent:      fields.proposalsSent,     // Fix 2: new column
          contracts_sent:      fields.contractsSent,     // Fix 2: new column
          closed_won_count:    fields.dealsAdvanced,
          notes: serverNotes,
        }),
      });

      // ── Secondary write: activity_log (audit trail, fire-and-forget) ─────────
      // One summary row for the full daily log save.
      void logActivity({
        action: "ae.daily_log",
        entity_type: "daily_log",
        entity_id: `${userId}:${date}`,
        payload: {
          date,
          callsMade:      fields.callsMade,
          emailsSent:     fields.emailsSent,
          meetingsHeld:   fields.meetingsHeld,
          demosGiven:     fields.demosGiven,
          proposalsSent:  fields.proposalsSent,
          contractsSent:  fields.contractsSent,
          dealsAdvanced:  fields.dealsAdvanced,
          accountEntryCount: accountEntries.length,
          hasNotes: notes.trim().length > 0,
        },
      });

      // Per-metric granular rows (only log when non-zero — reduces noise).
      if (fields.callsMade > 0) {
        void logActivity({
          action: "ae.call_logged",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.callsMade },
        });
      }
      if (fields.emailsSent > 0) {
        void logActivity({
          action: "ae.email_sent",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.emailsSent },
        });
      }
      if (fields.meetingsHeld > 0) {
        void logActivity({
          action: "ae.meeting_logged",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.meetingsHeld },
        });
      }
      if (fields.demosGiven > 0) {
        void logActivity({
          action: "ae.demo_logged",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.demosGiven },
        });
      }
      if (fields.proposalsSent > 0) {
        void logActivity({
          action: "ae.proposal_sent",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.proposalsSent },
        });
      }
      if (fields.contractsSent > 0) {
        void logActivity({
          action: "ae.contract_sent",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.contractsSent },
        });
      }
      if (fields.dealsAdvanced > 0) {
        void logActivity({
          action: "ae.deal_advanced",
          entity_type: "daily_log",
          entity_id: `${userId}:${date}`,
          payload: { date, count: fields.dealsAdvanced },
        });
      }

      // Per-account entries: one activity_log row per account touched.
      // Bug B fix: entity_id is the resolved account_id (integer) when available,
      // falling back to the account name string only when no FK match exists.
      for (const entry of accountEntries) {
        if (!entry.accountName.trim()) continue; // skip blank entries
        void logActivity({
          action: "ae.account_updated",
          entity_type: "account",
          // Use numeric account_id if picker resolved it; otherwise the name (legacy)
          entity_id: entry.account_id != null ? entry.account_id : entry.accountName.trim(),
          payload: {
            date,
            account_id: entry.account_id ?? null,  // Bug B fix: persist resolved FK
            accountName: entry.accountName.trim(),
            activityType: entry.activityType,
            outcomeNote: entry.outcomeNote || null,
          },
        });
      }

      // ── Local cache write (resilience — not primary store) ───────────────────
      saveAeLog(log);

      const timeStr = new Date(now).toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" });
      setSavedAt(timeStr);
      onSaved?.();
    } catch (err: any) {
      // On API failure, still save locally so the rep doesn't lose work.
      saveAeLog(log);
      const msg = err?.message ?? "Unknown error";
      console.error("[AeActivityLog] Supabase write failed:", msg);
      toast({
        title: "Save failed",
        description: "Your activity was saved locally. It will sync when the connection is restored.",
        variant: "destructive",
      });
      setSavedAt("save failed — try again");
    }
  }

  function handleReset() {
    setFields(emptyAeFields());
    setAccountEntries([]);
    setNotes("");
    setSavedAt(null);
    setDate(todayString());
  }

  const totalActivities =
    fields.callsMade + fields.emailsSent + fields.meetingsHeld + fields.demosGiven;

  const inner = (
    <div className="space-y-5">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div
        className="rounded-xl p-5 text-white"
        style={{ background: `linear-gradient(135deg, ${MI6_BLACK}, #1F2937)` }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">
              Mi6 Attilius · AE
            </div>
            <h1 className="text-2xl font-bold">Daily Activity Log</h1>
            <div className="text-neutral-500 text-sm mt-0.5">
              Self-reported · manually entered
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-semibold">
              Log Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-neutral-600 bg-neutral-800 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
        {totalActivities > 0 && (
          <div className="mt-3 flex gap-4 text-sm flex-wrap">
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.callsMade}</span> calls
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.meetingsHeld}</span> meetings
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.demosGiven}</span> demos
            </span>
            <span className="text-neutral-300">
              <span className="font-bold text-white tabular-nums">{fields.dealsAdvanced}</span> deals advanced
            </span>
          </div>
        )}
      </div>

      {/* ── Account-touching activity totals ────────────────────────────── */}
      <SectionCard title="Account-Touching Activity — Totals for the Day">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          <NumInput label="Calls Made" value={fields.callsMade} onChange={(v) => set("callsMade", v)} />
          <NumInput label="Emails Sent" value={fields.emailsSent} onChange={(v) => set("emailsSent", v)} />
          <NumInput label="Meetings Held" value={fields.meetingsHeld} onChange={(v) => set("meetingsHeld", v)} />
          <NumInput label="Demos Given" value={fields.demosGiven} onChange={(v) => set("demosGiven", v)} />
          <NumInput label="Proposals Sent" value={fields.proposalsSent} onChange={(v) => set("proposalsSent", v)} />
          <NumInput label="Contracts Sent" value={fields.contractsSent} onChange={(v) => set("contractsSent", v)} />
          <NumInput label="Deals Advanced" value={fields.dealsAdvanced} onChange={(v) => set("dealsAdvanced", v)} hint="Stage moved fwd" />
        </div>
      </SectionCard>

      {/* ── Per-account log ─────────────────────────────────────────────── */}
      <SectionCard title="Per-Account Log" accent={MI6_RED}>
        <div className="space-y-3">
          {accountEntries.length === 0 && (
            <div className="text-sm text-neutral-500 py-2">
              No account entries yet — use the button below to log activity for a specific account.
            </div>
          )}
          {accountEntries.map((entry, i) => (
            <AccountEntryCard
              key={entry.id}
              entry={entry}
              index={i}
              accounts={accountOptions}
              onChange={(updated) => updateEntry(entry.id, updated)}
              onRemove={() => removeEntry(entry.id)}
            />
          ))}
        </div>
        <button
          onClick={addAccountEntry}
          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed transition-all text-sm font-semibold"
          style={{ borderColor: `${MI6_RED}50`, color: MI6_RED }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = MI6_RED;
            e.currentTarget.style.background = `${MI6_RED}08`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = `${MI6_RED}50`;
            e.currentTarget.style.background = "";
          }}
        >
          <span className="text-lg leading-none">+</span>
          Log activity for an account
        </button>
      </SectionCard>

      {/* ── Notes ───────────────────────────────────────────────────────── */}
      <SectionCard title="Notes">
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setSavedAt(null); }}
          placeholder="Anything notable from today — key conversations, deal movements, buying signals, blockers…"
          rows={4}
          className="w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm text-neutral-700 placeholder-neutral-300 focus:outline-none resize-none"
          onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${MI6_RED}40`)}
          onBlur={(e) => (e.target.style.boxShadow = "")}
        />
        <div className="text-[10px] text-neutral-500 mt-1">Optional</div>
      </SectionCard>

      {/* ── Save bar ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 pb-4">
        <div className="flex items-center gap-3">
          {savedAt ? (
            <span className="text-sm font-semibold text-neutral-600">
              ✓ Saved at {savedAt}
            </span>
          ) : (hasFieldInput || hasNotes || hasEntries) ? (
            <span className="text-sm text-neutral-500">Unsaved changes</span>
          ) : (
            <span className="text-sm text-neutral-400">Ready to log activity</span>
          )}
          {savedAt && (
            <button
              onClick={handleReset}
              className="text-xs text-neutral-500 hover:text-neutral-600 underline underline-offset-2 transition-colors"
            >
              Clear &amp; restart
            </button>
          )}
        </div>
        <button
          onClick={handleSave}
          className="px-6 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider text-white shadow-sm transition-all hover:shadow-md active:scale-95"
          style={{ background: MI6_RED }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#B91C1C")}
          onMouseLeave={(e) => (e.currentTarget.style.background = MI6_RED)}
        >
          Save Today's Log
        </button>
      </div>
    </div>
  );

  if (modal) {
    return inner;
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "AE Activity Log" }]} pageTitle="AE Activity Log" pageMeta="Daily activity log">
      <BackBar href="/dashboards/daily" label="AE Dashboard" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">{inner}</main>
    </OperatorShell>
  );
}
